// Single source of truth for the description forms' extra validation, shared by
// the office dialogs (FindingFormDialog / FindingPlanum/ProfileFormDialog).

import { isCircularForm } from '@ast-data/shared';
import { locationLabel, locationFindingNumber } from './locationLabel';
import { compareNatural, compareText } from '../../utils/compare';

/**
 * Description fields that get the soft "recommended, empty" hint (befundator's
 * completeness warnings). Keyed by the form-field name.
 */
export const RECOMMENDED_PLANUM_FIELDS = new Set<string>([
    'length',
    'width',
    'form',
    'orientation',
    'color',
    'soilType',
    'boundaryClarity',
    'description'
]);
export const RECOMMENDED_PROFILE_FIELDS = new Set<string>([
    'width',
    'depth',
    'form',
    'orientation',
    'description'
]);

/**
 * Whether a Planum field carries the recommended-empty hint, read against the
 * Form the description states. A circle is measured by its Durchmesser, so
 * „kreisförmig" moves the hint onto `diameter` and off the other two — which
 * stay editable, they just stop being asked for.
 */
export function isPlanumFieldRecommended(name: string, form: string): boolean {
    if (isCircularForm(form)) {
        if (name === 'diameter') {
            return true;
        }
        if (name === 'length' || name === 'width') {
            return false;
        }
    }
    return RECOMMENDED_PLANUM_FIELDS.has(name);
}
export const RECOMMENDED_FINDING_FIELDS = new Set<string>(['interpretation']);

/**
 * The free-text stratigraphy cross-references. Each holds Befundnummern, so a
 * description may not point them back at the Befund it belongs to — a Befund
 * can't overlie itself. (`identicalWith` is no longer here: it became
 * description-ref arrays, and its self-reference guard lives in the picker.)
 */
export const STRATIGRAPHY_FIELDS = new Set<string>([
    'overliesOrCuts',
    'underliesOrIsCut',
    'contemporaryWith',
    // The pair a baulicher Bestand adds — free-text Befundnummern like the
    // three above, and checked for a self-reference the same way.
    'fills',
    'filledBy'
]);

/**
 * The Befundnummern a stratigraphy picker may offer: every one in the family
 * except the description's own Befund.
 */
export function stratigraphyOptions(
    findings: readonly { id: string; findingNumber: string }[],
    ownFindingId: string
): string[] {
    return [
        ...new Set(
            findings
                .filter(
                    (finding) =>
                        finding.id !== ownFindingId && !!finding.findingNumber
                )
                .map((finding) => finding.findingNumber)
        )
    ].sort((firstNumber, secondNumber) =>
        compareText(firstNumber, secondNumber)
    );
}

/**
 * The positional stratigraphy relations: they assert a physical overlap (or a
 * shared moment), which needs a shared place — their pickers offer only
 * descriptions on the SAME Fläche and the same Planum/Profil.
 */
export const POSITIONAL_STRATIGRAPHY_FIELDS = new Set<string>([
    'overliesOrCuts',
    'underliesOrIsCut',
    'contemporaryWith',
    'fills',
    'filledBy'
]);

/** Case-insensitive, trimmed equality for spatial slot values. */
function sameSlot(left: string, right: string): boolean {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** "Bef. 7000, Fl. 1, Pl. 1" — how a stratigraphy option names its slot. */
export function strataLabel(
    findingNumber: string,
    kind: 'Planum' | 'Profil',
    area: string,
    number: string
): string {
    return locationLabel({
        findingNumber,
        area,
        planumNumber: kind === 'Planum' ? number : undefined,
        profileNumber: kind === 'Profil' ? number : undefined
    });
}

/**
 * Options for the positional stratigraphy pickers: the OTHER Befunde described
 * on the same Fläche and the same Planum-/Profilnummer. Labelled as the Lage
 * line ("Bef. 7000, Fl. 1, Pl. 1"), but only the Befundnummer is STORED (see
 * strataOptionNumber) — the ExcaBook importer splits these fields on ';'/','
 * and reads every token as a Befundnummer.
 */
export function siblingStrataOptions({
    siblings,
    findings,
    ownFindingId,
    area,
    number,
    kind
}: {
    /** The family's sibling descriptions (Planums OR Profils, pre-mapped). */
    siblings: readonly { findingId: string; area: string; number: string }[];
    findings: readonly { id: string; findingNumber: string }[];
    ownFindingId: string;
    area: string;
    /** The Planum-/Profilnummer of the description being edited. */
    number: string;
    kind: 'Planum' | 'Profil';
}): string[] {
    const numberById = new Map(
        findings.map((finding) => [finding.id, finding.findingNumber])
    );
    const seen = new Set<string>();
    const options: string[] = [];
    for (const sibling of siblings) {
        if (sibling.findingId === ownFindingId) {
            continue;
        }
        if (
            !sameSlot(sibling.area, area) ||
            !sameSlot(sibling.number, number)
        ) {
            continue;
        }
        const findingNumber = numberById.get(sibling.findingId) ?? '';
        if (!findingNumber || seen.has(findingNumber)) {
            continue;
        }
        seen.add(findingNumber);
        options.push(
            strataLabel(findingNumber, kind, sibling.area, sibling.number)
        );
    }
    return options.sort((firstOption, secondOption) =>
        compareNatural(firstOption, secondOption)
    );
}

/** The bare Befundnummer of a picked stratigraphy option (the stored value). */
export function strataOptionNumber(option: string): string {
    return locationFindingNumber(option);
}

/**
 * Options for "Teil von / identisch mit": EVERY other Befund's description —
 * Planum and Profil rows alike, any Fläche — labelled like the positional
 * options so the picker reads the same everywhere.
 */
export function findingDescriptionOptions({
    planums,
    profiles,
    findings,
    ownFindingId
}: {
    planums: readonly { findingId: string; area: string; number: string }[];
    profiles: readonly { findingId: string; area: string; number: string }[];
    findings: readonly { id: string; findingNumber: string }[];
    ownFindingId: string;
}): string[] {
    const numberById = new Map(
        findings.map((finding) => [finding.id, finding.findingNumber])
    );
    const seen = new Set<string>();
    const options: string[] = [];
    const kinds: ['Planum' | 'Profil', typeof planums][] = [
        ['Planum', planums],
        ['Profil', profiles]
    ];
    for (const [kind, rows] of kinds) {
        for (const row of rows) {
            if (row.findingId === ownFindingId) {
                continue;
            }
            const findingNumber = numberById.get(row.findingId) ?? '';
            if (!findingNumber) {
                continue;
            }
            const label = strataLabel(
                findingNumber,
                kind,
                row.area,
                row.number
            );
            if (seen.has(label)) {
                continue;
            }
            seen.add(label);
            options.push(label);
        }
    }
    return options.sort((firstOption, secondOption) =>
        compareNatural(firstOption, secondOption)
    );
}

/** The Befundnummern of a stratigraphy field value ("5, 7" / "5; 7"). */
export function splitNumbers(text: string): string[] {
    return (text || '')
        .split(/[;,]/)
        .map((part) => part.trim())
        .filter(Boolean);
}

/** Join Befundnummern back into the stored comma form. */
export function joinNumbers(numbers: readonly string[]): string {
    return numbers.join(', ');
}

function containsNumber(list: readonly string[], number: string): boolean {
    const needle = number.trim().toLowerCase();
    return list.some((entry) => entry.trim().toLowerCase() === needle);
}

export interface StrataSibling {
    id: string;
    findingId: string;
    area: string;
    /** The Planum-/Profilnummer. */
    slotNumber: string;
    overliesOrCuts: string;
    underliesOrIsCut: string;
    contemporaryWith: string;
    fills: string;
    filledBy: string;
}

/** One side's positional values (with the slot they applied to). */
export interface StrataState {
    area: string;
    slotNumber: string;
    overliesOrCuts: string;
    underliesOrIsCut: string;
    contemporaryWith: string;
    fills: string;
    filledBy: string;
}

/**
 * The two DIRECTED positional relations and their inverses: A over B means B
 * under A, one edge at a time. "Zeitgleich mit" is not here — it is symmetric,
 * and symmetric means a clique (see the contemporaryWith block below), which the
 * edge-by-edge mirror cannot express.
 */
const INVERSE_RELATIONS: [
    keyof StrataState & keyof StrataSibling,
    keyof StrataState & keyof StrataSibling
][] = [
    ['overliesOrCuts', 'underliesOrIsCut'],
    ['underliesOrIsCut', 'overliesOrCuts'],
    // „verfüllt" / „verfüllt durch" — directed just as over/under is, and
    // mirrored by the same rule: A verfüllt B means B ist verfüllt durch A.
    // The fields exist on EVERY description, not just on a baulicher Bestand,
    // precisely so this mirror always has somewhere to land.
    ['fills', 'filledBy'],
    ['filledBy', 'fills']
];

/**
 * Mirror positional stratigraphy edits onto the referenced Befunde: adding B to
 * A's "liegt unter" writes A into B's "liegt über" (on B's description of the
 * SAME Fläche+Planum/Profil); zeitgleich mirrors onto itself. Diffs `before`
 * against `after`, so untouched relations never write — removals resolve against
 * the BEFORE slot, additions against the AFTER one, since the record may have
 * moved.
 */
export async function syncInverseStratigraphy({
    siblings,
    findings,
    ownFindingId,
    before,
    after,
    applyPatch
}: {
    siblings: readonly StrataSibling[];
    findings: readonly { id: string; findingNumber: string }[];
    ownFindingId: string;
    before: StrataState;
    after: StrataState;
    applyPatch: (id: string, patch: Record<string, string>) => Promise<void>;
}): Promise<void> {
    const ownNumber =
        findings.find((finding) => finding.id === ownFindingId)
            ?.findingNumber ?? '';
    if (!ownNumber.trim()) {
        return;
    }
    const numberById = new Map(
        findings.map((finding) => [finding.id, finding.findingNumber])
    );
    // Sibling rows by Befundnummer, per slot (removals may target the old one).
    function slotSiblings(
        area: string,
        slotNumber: string
    ): Map<string, StrataSibling> {
        const byNumber = new Map<string, StrataSibling>();
        for (const sibling of siblings) {
            if (sibling.findingId === ownFindingId) {
                continue;
            }
            if (
                !sameSlot(sibling.area, area) ||
                !sameSlot(sibling.slotNumber, slotNumber)
            ) {
                continue;
            }
            const number = numberById.get(sibling.findingId) ?? '';
            if (number.trim()) {
                byNumber.set(number.trim().toLowerCase(), sibling);
            }
        }
        return byNumber;
    }
    const beforeSlot = slotSiblings(before.area, before.slotNumber);
    const afterSlot = slotSiblings(after.area, after.slotNumber);

    // Accumulate per-sibling patches so one save writes each sibling once.
    const patches = new Map<string, Record<string, string>>();
    function pendingList(sibling: StrataSibling, field: string): string[] {
        const pending = patches.get(sibling.id)?.[field];
        return splitNumbers(
            pending ?? (sibling[field as keyof StrataSibling] as string)
        );
    }
    function setPending(sibling: StrataSibling, field: string, value: string) {
        patches.set(sibling.id, {
            ...(patches.get(sibling.id) ?? {}),
            [field]: value
        });
    }

    for (const [field, inverse] of INVERSE_RELATIONS) {
        const beforeNumbers = splitNumbers(before[field]);
        const afterNumbers = splitNumbers(after[field]);
        for (const number of afterNumbers) {
            if (containsNumber(beforeNumbers, number)) {
                continue;
            }
            const sibling = afterSlot.get(number.trim().toLowerCase());
            if (!sibling) {
                continue;
            }
            const list = pendingList(sibling, inverse);
            if (!containsNumber(list, ownNumber)) {
                setPending(sibling, inverse, joinNumbers([...list, ownNumber]));
            }
        }
        for (const number of beforeNumbers) {
            if (containsNumber(afterNumbers, number)) {
                continue;
            }
            const sibling = beforeSlot.get(number.trim().toLowerCase());
            if (!sibling) {
                continue;
            }
            const list = pendingList(sibling, inverse);
            if (containsNumber(list, ownNumber)) {
                setPending(
                    sibling,
                    inverse,
                    joinNumbers(
                        list.filter(
                            (entry) =>
                                entry.trim().toLowerCase() !==
                                ownNumber.trim().toLowerCase()
                        )
                    )
                );
            }
        }
    }

    /**
     * "Zeitgleich mit" is a clique: A, B and C being contemporary means each of
     * them names the other two. So every Befund the field lists gets the WHOLE
     * group minus itself — not just the own number, and not only the ones just
     * added, because a sibling that was already listed is missing the newcomer
     * just as much as the newcomer is missing it.
     */
    const contemporaryGroup = [
        ownNumber,
        ...splitNumbers(after.contemporaryWith)
    ].filter(
        (number, index, all) =>
            number.trim() !== '' &&
            all.findIndex(
                (other) =>
                    other.trim().toLowerCase() === number.trim().toLowerCase()
            ) === index
    );
    for (const number of contemporaryGroup) {
        if (number.trim().toLowerCase() === ownNumber.trim().toLowerCase()) {
            continue;
        }
        const sibling = afterSlot.get(number.trim().toLowerCase());
        if (!sibling) {
            continue;
        }
        const list = pendingList(sibling, 'contemporaryWith');
        const missing = contemporaryGroup.filter(
            (member) =>
                member.trim().toLowerCase() !== number.trim().toLowerCase() &&
                !containsNumber(list, member)
        );
        if (missing.length > 0) {
            setPending(
                sibling,
                'contemporaryWith',
                joinNumbers([...list, ...missing])
            );
        }
    }
    // Dropping a Befund from the field takes the own number off it — one edge,
    // because "A is not contemporary with D" says nothing about B and C.
    for (const number of splitNumbers(before.contemporaryWith)) {
        if (containsNumber(splitNumbers(after.contemporaryWith), number)) {
            continue;
        }
        const sibling = beforeSlot.get(number.trim().toLowerCase());
        if (!sibling) {
            continue;
        }
        const list = pendingList(sibling, 'contemporaryWith');
        if (containsNumber(list, ownNumber)) {
            setPending(
                sibling,
                'contemporaryWith',
                joinNumbers(
                    list.filter(
                        (entry) =>
                            entry.trim().toLowerCase() !==
                            ownNumber.trim().toLowerCase()
                    )
                )
            );
        }
    }

    for (const [id, patch] of patches) {
        await applyPatch(id, patch);
    }
}

export interface DescriptionRef {
    kind: 'planum' | 'profile';
    id: string;
}

/**
 * The DESCRIPTION-level relations that are SYMMETRIC, in the strong sense: a
 * group named together is a CLIQUE, so a Planum identical with three others
 * leaves all four carrying the other three. "Teil von" is deliberately not one
 * — A being part of B says nothing about B being part of A.
 */
export type SymmetricRelation = 'identicalWith' | 'inProfileWith';

export interface RelationRow {
    id: string;
    kind: 'planum' | 'profile';
    identicalWithPlanumIds: string[];
    identicalWithProfileIds: string[];
    inProfileWithPlanumIds: string[];
    inProfileWithProfileIds: string[];
}

function relationField(
    relation: SymmetricRelation,
    kind: 'planum' | 'profile'
): keyof RelationRow {
    return (
        kind === 'planum' ? `${relation}PlanumIds` : `${relation}ProfileIds`
    ) as keyof RelationRow;
}

function toRefs(refs: {
    planumIds: readonly string[];
    profileIds: readonly string[];
}): DescriptionRef[] {
    return [
        ...refs.planumIds.map((id) => ({ kind: 'planum' as const, id })),
        ...refs.profileIds.map((id) => ({ kind: 'profile' as const, id }))
    ];
}

function sameRef(left: DescriptionRef, right: DescriptionRef): boolean {
    return left.kind === right.kind && left.id === right.id;
}

export function dedupeRefs(refs: DescriptionRef[]): DescriptionRef[] {
    return refs.filter(
        (ref, index, all) =>
            all.findIndex((other) => sameRef(other, ref)) === index
    );
}

/**
 * Close a symmetric relation over the group it names: saving A with "identisch
 * mit B, C, D" makes the four a clique, B ending up naming A, C and D. Half of
 * it written down is a Befund that reads differently depending on which
 * description you open.
 *
 * Additive and idempotent. Removal stays one edge at a time — taking D out of
 * A's field takes A out of D's and leaves B and C alone, because "A is not
 * identical with D" says nothing about them.
 */
export async function syncSymmetricRefs({
    relation,
    rows,
    own,
    before,
    after,
    applyPatch
}: {
    relation: SymmetricRelation;
    /** Every description row of the family, both kinds. */
    rows: readonly RelationRow[];
    own: DescriptionRef;
    before: { planumIds: readonly string[]; profileIds: readonly string[] };
    after: { planumIds: readonly string[]; profileIds: readonly string[] };
    applyPatch: (
        kind: 'planum' | 'profile',
        id: string,
        patch: Record<string, string[]>
    ) => Promise<void>;
}): Promise<void> {
    const rowByKey = new Map(rows.map((row) => [`${row.kind}:${row.id}`, row]));
    const beforeRefs = toRefs(before);
    const afterRefs = dedupeRefs(toRefs(after));
    // Everybody the group holds — the saved description and what it names.
    const group = dedupeRefs([own, ...afterRefs]);

    // Every member gets the rest of the group. Runs over the WHOLE group, not
    // only over what was just added: a member that was already there is missing
    // the newcomer just as much as the newcomer is missing it.
    for (const ref of afterRefs) {
        if (sameRef(ref, own)) {
            continue;
        }
        const target = rowByKey.get(`${ref.kind}:${ref.id}`);
        if (!target) {
            continue;
        }
        const patch: Record<string, string[]> = {};
        for (const kind of ['planum', 'profile'] as const) {
            const field = relationField(relation, kind);
            const current = (target[field] as string[]) ?? [];
            const missing = group
                .filter(
                    (member) =>
                        member.kind === kind &&
                        !sameRef(member, ref) &&
                        !current.includes(member.id)
                )
                .map((member) => member.id);
            if (missing.length > 0) {
                patch[field] = [...current, ...missing];
            }
        }
        if (Object.keys(patch).length > 0) {
            await applyPatch(ref.kind, ref.id, patch);
        }
    }

    for (const ref of beforeRefs) {
        if (
            afterRefs.some((entry) => sameRef(entry, ref)) ||
            sameRef(ref, own)
        ) {
            continue;
        }
        const target = rowByKey.get(`${ref.kind}:${ref.id}`);
        if (!target) {
            continue;
        }
        const field = relationField(relation, own.kind);
        const current = (target[field] as string[]) ?? [];
        if (current.includes(own.id)) {
            await applyPatch(ref.kind, ref.id, {
                [field]: current.filter((id) => id !== own.id)
            });
        }
    }
}

/**
 * True when a stratigraphy value names the own Befund. The pickers write one
 * number at a time, but the fields are free text and the Amt's forms list
 * several separated by commas, so every listed number is checked.
 */
export function isSelfReference(
    value: string,
    ownFindingNumber: string
): boolean {
    const own = ownFindingNumber.trim().toLowerCase();
    if (!own) {
        return false;
    }
    return value
        .split(/[,;]/)
        .some((part) => part.trim().toLowerCase() === own);
}

/** Numeric measure fields (office uses the Zod schema for the actual check). */
export const MEASURE_PLANUM_FIELDS = new Set<string>([
    'length',
    'width',
    'diameter',
    'depth'
]);
export const MEASURE_PROFILE_FIELDS = new Set<string>(['width', 'depth']);

/**
 * Elevation fields (heights above sea level, NHN). Unlike the length measures
 * above (cm), these read in metres — so they carry an "in m" hint, not "in cm".
 */
export const HEIGHT_FIELDS = new Set<string>(['heightTop', 'heightBottom']);

// --- Teile: the slot a description occupies within its Befund ----------------

/** One sibling description, reduced to what decides whether a slot is free. */
export interface DescriptionSlot {
    id: string;
    findingId: string;
    /** Planum-/Profilnummer. */
    number: string;
    /** The Teil mark; empty for an undivided description. */
    partLabel: string;
}

/** Where a slot clash has to be reported, and what it reads as. */
export interface SlotIssue {
    /** Which input carries the error — the number, or the Teil beside it. */
    target: 'number' | 'part';
    /** A validation rule code (see i18n/validationMessages). */
    code: string;
}

function same(left: string, right: string): boolean {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * Whether this description may occupy (Befund, Nummer, Teil). A Planum of a
 * Befund is EITHER one undivided description OR a set of Teile, never both, or
 * two records would both claim to describe all of Planum 1. So three clashes:
 *
 * - the same Teil twice (or the same undivided Planum twice)  -> duplicate
 * - no Teil while the siblings have one                       -> partRequired
 * - a Teil while an undivided description already stands      -> partConflict
 *
 * `siblings` is the FAMILY's descriptions of this kind — a shared Befund's rows
 * may sit in any project of it. Returns null when the slot is free.
 */
export function describedSlotIssue(
    self: DescriptionSlot,
    siblings: readonly DescriptionSlot[]
): SlotIssue | null {
    const others = siblings.filter(
        (row) =>
            row.id !== self.id &&
            row.findingId === self.findingId &&
            same(row.number, self.number)
    );
    if (others.length === 0) {
        return null;
    }
    if (others.some((row) => same(row.partLabel, self.partLabel))) {
        // Without a Teil in play this is the old "Planum 2 described twice",
        // which has always been reported on the number.
        return self.partLabel.trim()
            ? { target: 'part', code: 'duplicatePart' }
            : { target: 'number', code: 'duplicateNumber' };
    }
    if (!self.partLabel.trim()) {
        return { target: 'part', code: 'partRequired' };
    }
    if (others.some((row) => !row.partLabel.trim())) {
        return { target: 'part', code: 'partConflict' };
    }
    return null;
}

/**
 * The sibling Teile a renamed description takes with it — their ids, to be
 * patched to the new number.
 *
 * The Teile of a Planum are Teile OF THAT NUMBER: "1, Teil A" and "1, Teil B"
 * are one description in two pieces, so renaming one alone would tear it into a
 * Planum 3 Teil A and a leftover Planum 1 Teil B.
 *
 * Nothing travels when the number did not change, when this description carries
 * no Teil mark, or when it moved to another Befund — that is a move, not a
 * rename. Only marked siblings follow; an unmarked row at the old number is the
 * `partConflict` the validation already reports.
 */
export function renamedPartSiblings({
    self,
    previous,
    siblings
}: {
    /** The description as SAVED — its new number and its Teil mark. */
    self: DescriptionSlot;
    /** Where it sat before the save. */
    previous: Pick<DescriptionSlot, 'findingId' | 'number'>;
    siblings: readonly DescriptionSlot[];
}): string[] {
    if (
        same(self.number, previous.number) ||
        self.findingId !== previous.findingId ||
        !self.partLabel.trim()
    ) {
        return [];
    }
    return siblings
        .filter(
            (row) =>
                row.id !== self.id &&
                row.findingId === previous.findingId &&
                same(row.number, previous.number) &&
                row.partLabel.trim() !== ''
        )
        .map((row) => row.id);
}

/**
 * The Teile of one Planum/Profil, in reading order — what the Teil bar of the
 * form lists. Undivided descriptions yield an empty list: there is no bar to
 * show until a second description of the same number exists.
 */
export function siblingParts<T extends DescriptionSlot>(
    self: Pick<DescriptionSlot, 'findingId' | 'number'>,
    siblings: readonly T[]
): T[] {
    return siblings
        .filter(
            (row) =>
                row.findingId === self.findingId &&
                same(row.number, self.number)
        )
        .sort((firstRow, secondRow) =>
            compareNatural(firstRow.partLabel, secondRow.partLabel)
        );
}
